import { enhanceProductOptions } from './product-options.mjs';

/** Native WooCommerce variation forms, scoped to the drawer and loaded on demand. */
export const prepareCartRecommendationForm = (form, productId) => {
    if (!form?.matches('form.variations_form') || form.dataset.product_id !== String(productId)) return null;
    const ids = new Map();
    form.querySelectorAll('[id]').forEach((element) => ids.set(element.id, `cart-recommendation-${productId}-${element.id}`));
    form.querySelectorAll('[id], [for], [aria-describedby], [aria-labelledby]').forEach((element) => {
        ['id', 'for', 'aria-describedby', 'aria-labelledby'].forEach((attribute) => {
            if (!element.hasAttribute(attribute)) return;
            element.setAttribute(attribute, element.getAttribute(attribute).split(/\s+/).map((id) => ids.get(id) || id).join(' '));
        });
    });
    form.querySelectorAll('select[name^="attribute_"]').forEach((select) => {
        const choices = [...select.options].filter((option) => option.value && !option.disabled);
        if (!select.value && choices.length === 1) select.value = choices[0].value;
    });
    return form;
};

export const initializeCartRecommendations = (dialog, { purchase, isBusy, ajaxUrl }) => {
    const $ = window.jQuery;
    let request;
    let pendingCard;
    const focusOptions = (form) => {
        const groups = [...form.querySelectorAll('.ProductOptions__group')];
        if (groups.length) {
            const group = groups.find((item) => !item.querySelector('input:checked')) || groups[0];
            (group.querySelector('input:checked:not(:disabled)') || group.querySelector('input:not(:disabled)'))?.focus({ preventScroll: true });
        } else {
            const selects = [...form.querySelectorAll('select[name^="attribute_"]')];
            (selects.find((select) => !select.value) || selects[0])?.focus({ preventScroll: true });
        }
    };
    const feedback = (card, message = '') => {
        const node = card.querySelector('[data-cart-recommendation-feedback]');
        node.textContent = message;
        node.hidden = !message;
    };
    const collapse = (card, restoreFocus = false) => {
        card.querySelector('[data-cart-recommendation-options]').hidden = true;
        const trigger = card.querySelector('[data-cart-recommendation-action]');
        trigger.setAttribute('aria-expanded', 'false');
        if (pendingCard === card) {
            request?.abort();
            card.querySelector('[data-cart-recommendation-form]').removeAttribute('aria-busy');
            delete trigger.dataset.state;
        }
        if (restoreFocus) trigger.focus({ preventScroll: true });
    };
    const bindForm = (card, form) => {
        const button = form.querySelector('.single_add_to_cart_button');
        if (!button) throw new Error('Missing purchase button');
        const section = card.closest('[data-cart-recommendations]');
        const price = card.querySelector('[data-cart-recommendation-price]');
        const image = card.querySelector('[data-cart-recommendation-image]');
        const initial = { price: price.innerHTML, image: image.innerHTML };
        button.dataset.cartFocus = `recommendation-${card.dataset.cartRecommendation}-submit`;
        const choose = () => {
            feedback(card, section.dataset.choose);
            focusOptions(form);
        };
        // Stop WooCommerce's native alert and the legacy document-level cart submit handler.
        form.addEventListener('click', (event) => {
            if (event.target.closest('.single_add_to_cart_button') && (button.disabled || button.classList.contains('disabled'))) {
                event.preventDefault(); event.stopImmediatePropagation(); choose();
            }
        }, true);
        form.addEventListener('submit', (event) => {
            event.preventDefault(); event.stopPropagation();
            if (isBusy()) return;
            if (button.disabled || button.classList.contains('disabled') || !Number(form.querySelector('[name="variation_id"]')?.value)) { choose(); return; }
            feedback(card);
            const data = new FormData(form);
            form.querySelectorAll('.ProductOptions__input').forEach((input) => data.delete(input.name));
            if (!data.has('add-to-cart') && button.name === 'add-to-cart') data.set(button.name, button.value);
            purchase(Object.fromEntries(data), button);
        });
        $(form).on('found_variation.ovaliaCartRecommendation', (event, variation) => {
            price.innerHTML = variation.price_html || initial.price;
            const photo = image.querySelector('img');
            if (photo && variation.image?.src) {
                photo.srcset = variation.image.srcset || '';
                photo.src = variation.image.src;
            } else image.innerHTML = initial.image;
            feedback(card);
        }).on('reset_data.ovaliaCartRecommendation', () => {
            price.innerHTML = initial.price; image.innerHTML = initial.image;
        }).on('woocommerce_variation_has_changed.ovaliaCartRecommendation', () => feedback(card));
        $(form).wc_variation_form();
        const options = enhanceProductOptions(form, { assets: section.dataset.assets }, () => feedback(card));
        if (options) {
            form.classList.add('CartRecommendation__form--enhanced');
            // Keep WooCommerce's reset action available outside the hidden legacy swatches.
            const reset = form.querySelector('.reset_variations');
            if (reset) options.after(reset);
            const resetAlert = form.querySelector('.reset_variations_alert');
            if (resetAlert) (reset || options).after(resetAlert);
            $(form).on('reset_focus.ovaliaCartRecommendation', () => focusOptions(form));
        }
        $(form).trigger('check_variations');
    };
    const expand = async (card) => {
        const panel = card.querySelector('[data-cart-recommendation-options]');
        if (!panel.hidden) { collapse(card); return; }
        dialog.querySelectorAll('[data-cart-recommendation-options]:not([hidden])').forEach((other) => collapse(other.closest('[data-cart-recommendation]')));
        const trigger = card.querySelector('[data-cart-recommendation-action]');
        const host = panel.querySelector('[data-cart-recommendation-form]');
        const section = card.closest('[data-cart-recommendations]');
        panel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        feedback(card);
        if (host.querySelector('form')) { focusOptions(host.querySelector('form')); return; }
        request?.abort();
        const controller = new AbortController();
        request = controller; pendingCard = card;
        host.innerHTML = '<p class="CartRecommendation__loading" role="status"></p>';
        host.querySelector('p').textContent = section.dataset.loading;
        host.setAttribute('aria-busy', 'true');
        trigger.dataset.state = 'loading';
        const timer = setTimeout(() => controller.abort(), 15000);
        try {
            const url = new URL(ajaxUrl, window.location.href);
            url.searchParams.set('action', 'ovalia_product_quick_buy');
            url.searchParams.set('product_id', card.dataset.cartRecommendation);
            const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal, cache: 'no-store' });
            const result = await response.json();
            if (!card.isConnected || panel.hidden || request !== controller) return;
            if (!response.ok || !result.success || typeof result.data?.html !== 'string') throw new Error('Invalid variation preview');
            const preview = new DOMParser().parseFromString(result.data.html, 'text/html');
            const form = prepareCartRecommendationForm(preview.querySelector('form.variations_form'), card.dataset.cartRecommendation);
            if (!form) throw new Error('Invalid product form');
            host.replaceChildren(form);
            bindForm(card, form);
            if (dialog.open) focusOptions(form);
        } catch (_) {
            if (!card.isConnected || panel.hidden || request !== controller) return;
            host.replaceChildren();
            collapse(card);
            feedback(card, section.dataset.error);
            if (dialog.open) trigger.focus({ preventScroll: true });
        } finally {
            clearTimeout(timer);
            if (request === controller) {
                request = null; pendingCard = null;
                host.removeAttribute('aria-busy'); delete trigger.dataset.state;
            }
        }
    };
    dialog.addEventListener('click', (event) => {
        const card = event.target.closest('[data-cart-recommendation]');
        if (!card) return;
        if (event.target.closest('[data-cart-recommendation-cancel]')) { collapse(card, true); return; }
        const trigger = event.target.closest('[data-cart-recommendation-action]');
        if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        if (trigger.dataset.cartRecommendationAction === 'choose' && !$?.fn.wc_variation_form) return;
        event.preventDefault();
        if (isBusy()) return;
        if (trigger.dataset.cartRecommendationAction === 'choose') { expand(card); return; }
        feedback(card);
        purchase({ 'add-to-cart': card.dataset.cartRecommendation, quantity: trigger.dataset.quantity || '1' }, trigger);
    });
    dialog.addEventListener('close', () => {
        if (pendingCard?.isConnected) collapse(pendingCard);
        request?.abort();
    });
    new MutationObserver(() => {
        if (pendingCard && !pendingCard.isConnected) request?.abort();
    }).observe(dialog, { childList: true, subtree: true });
};
